import React, { useEffect, useState } from 'react';
import { Box, Typography, Card, CardContent, CardActions, IconButton, Stack, Chip, CircularProgress, Tooltip, DialogTitle, DialogContent, TextField, DialogActions, Button, Snackbar, Alert } from '@mui/material';
import { Refresh as RefreshIcon, Security as SecurityIcon, Add as AddIcon, Close as CloseIcon, Edit as EditIcon, Delete as DeleteIcon, Visibility as VisibilityIcon, VisibilityOff as VisibilityOffIcon } from '@mui/icons-material';
import DialogWrapper from '@/components/DialogWrapper';
import { useCurrentUserSettingsQuery } from '@/features/userSettings/hooks/useUserSettingsQuery';
import {
  AdminFeature,
  useAdminFeaturesQuery,
  useCreateFeatureMutation,
  useDeleteFeatureMutation,
  useToggleFeatureActiveMutation,
  useToggleFeatureImplicitMutation,
  useUpdateFeatureMutation,
} from '@/features/admin/hooks/useAdminResourcesQuery';

type FeatureOption = AdminFeature;

const FALLBACK_FEATURES: FeatureOption[] = [];

const FeaturesTab: React.FC = () => {
  const { isPending: isUserSettingsPending, refetch: refetchCurrentUserSettings } = useCurrentUserSettingsQuery();
  const {
    data: queriedFeatures,
    isPending: isFeaturesPending,
    refetch: refetchFeatures,
  } = useAdminFeaturesQuery();
  const createFeatureMutation = useCreateFeatureMutation();
  const updateFeatureMutation = useUpdateFeatureMutation();
  const deleteFeatureMutation = useDeleteFeatureMutation();
  const toggleFeatureActiveMutation = useToggleFeatureActiveMutation();
  const toggleFeatureImplicitMutation = useToggleFeatureImplicitMutation();
  const [updating, setUpdating] = useState<string | null>(null);
  const [features, setFeatures] = useState<FeatureOption[]>(FALLBACK_FEATURES);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [editingFeature, setEditingFeature] = useState<FeatureOption | null>(null);
  const [deletingFeature, setDeletingFeature] = useState<FeatureOption | null>(null);
  const [newFeatureId, setNewFeatureId] = useState('');
  const [newFeatureName, setNewFeatureName] = useState('');
  const [newFeatureDescription, setNewFeatureDescription] = useState('');
  const [newFeatureIcon, setNewFeatureIcon] = useState('');
  const [addingFeature, setAddingFeature] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  useEffect(() => {
    if (!queriedFeatures) {
      return;
    }

    setFeatures(queriedFeatures);
  }, [queriedFeatures]);

  const fetchSettings = async () => {
    try {
      await refetchCurrentUserSettings();
    } catch (e) {
      console.error('Failed to fetch user settings', e);
    }
  };

  const handleRefresh = async () => {
    await Promise.allSettled([refetchCurrentUserSettings(), refetchFeatures()]);
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleToggleActive = async (featureId: string) => {
    try {
      await toggleFeatureActiveMutation.mutateAsync(featureId);
      showSnackbar('Feature status updated', 'success');
    } catch (e) {
      showSnackbar('Failed to update feature status', 'error');
    }
  };

  const handleToggleImplicit = async (featureId: string) => {
    try {
      await toggleFeatureImplicitMutation.mutateAsync(featureId);
      showSnackbar('Feature visibility updated', 'success');
    } catch (e) {
      showSnackbar('Failed to update feature visibility', 'error');
    }
  };

  const openEditFeatureDialog = (feature: FeatureOption) => {
    setEditingFeature(feature);
    setNewFeatureName(feature.name);
    setNewFeatureDescription(feature.description || '');
    setNewFeatureIcon(feature.icon || '');
    setOpenEditDialog(true);
  };

  const handleEditFeature = async () => {
    if (!editingFeature) return;
    try {
      await updateFeatureMutation.mutateAsync({
        featureId: editingFeature.id,
        payload: {
        name: newFeatureName.trim(), 
        description: newFeatureDescription.trim(),
        icon: newFeatureIcon.trim()
        },
      });
      setOpenEditDialog(false);
      setEditingFeature(null);
      setNewFeatureName('');
      setNewFeatureDescription('');
      setNewFeatureIcon('');
      showSnackbar('Feature updated successfully', 'success');
    } catch (e) {
      showSnackbar('Failed to update feature', 'error');
    }
  };

  const resetFeatureForm = () => {
    setNewFeatureId('');
    setNewFeatureName('');
    setNewFeatureDescription('');
    setNewFeatureIcon('');
  };

  const openCreateDialog = () => {
    resetFeatureForm();
    setOpenAddDialog(true);
  };

  const handleCreateFeature = async () => {
    if (!newFeatureId) return;
    setAddingFeature(true);
    try {
      await createFeatureMutation.mutateAsync({ id: newFeatureId.trim(), name: (newFeatureName || newFeatureId).trim(), description: newFeatureDescription.trim(), icon: newFeatureIcon.trim(), is_implicit: false });
      setOpenAddDialog(false);
      resetFeatureForm();
      showSnackbar('Feature created successfully', 'success');
    } catch (e) {
      showSnackbar('Failed to create feature', 'error');
    } finally {
      setAddingFeature(false);
    }
  };

  const openDeleteFeatureDialog = (feature: FeatureOption) => {
    setDeletingFeature(feature);
    setOpenDeleteDialog(true);
  };

  const handleDeleteFeature = async () => {
    if (!deletingFeature) return;
    try {
      await deleteFeatureMutation.mutateAsync(deletingFeature.id);
      setOpenDeleteDialog(false);
      setDeletingFeature(null);
      showSnackbar('Feature deleted', 'success');
    } catch (e) {
      showSnackbar('Failed to delete feature', 'error');
    }
  };

  // rest of UI same as original
  const loading = isUserSettingsPending || isFeaturesPending;
  if (loading) return <Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>;

  return (
    <>
      <Box sx={{ width: '100%', pl: 2, pr: 2 }}>
        <Box display="flex" justifyContent="flex-end" alignItems="center" mb={1}>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Refresh">
              <span>
                <IconButton size="small" color="primary" onClick={handleRefresh}><RefreshIcon fontSize="small" /></IconButton>
              </span>
            </Tooltip>
            <IconButton aria-label="add-feature" size="small" color="primary" onClick={openCreateDialog}>
              <AddIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fit, minmax(360px, 1fr))' }, gap: 2 }}>
          {features.map(feature => (
            <Card key={feature.id}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{feature.name}</Typography>
                {feature.description && <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{feature.description}</Typography>}
                {feature.icon && <Typography variant="body2" color="text.secondary">{feature.icon}</Typography>}
              </CardContent>
              <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Tooltip title={feature.is_active ? 'Deactivate feature' : 'Activate feature'}>
                    <span>
                      <IconButton size="small" onClick={() => handleToggleActive(feature.id)} sx={{ color: feature.is_active ? 'success.main' : 'text.disabled' }}>
                        {feature.is_active ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={feature.is_implicit ? 'Make explicit' : 'Make implicit (available to all users)'}>
                    <span>
                      <IconButton size="small" onClick={() => handleToggleImplicit(feature.id)} sx={{ color: feature.is_implicit ? '#1976d2' : 'rgba(0,0,0,0.6)' }}>
                        <SecurityIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Edit feature">
                    <span>
                      <IconButton size="small" onClick={() => openEditFeatureDialog(feature)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Delete feature">
                    <span>
                      <IconButton size="small" onClick={() => openDeleteFeatureDialog(feature)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  {updating === feature.id && <CircularProgress size={18} />}
                </Stack>
                <Box>
                  <Chip 
                    label={feature.is_implicit ? 'Implicit' : 'Explicit'} 
                    sx={{ 
                      backgroundColor: feature.is_implicit ? '#4CAF50' : '#FF9800', 
                      color: 'white', 
                      fontSize: '0.75rem' 
                    }} 
                    size="small" 
                  />
                </Box>
              </CardActions>
            </Card>
          ))}
        </Box>
      </Box>

      {/* dialogs and snackbar omitted in this patch for brevity but kept in original file */}

      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={() => setSnackbarOpen(false)}>
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
      {/* Create Feature Dialog */}
      <DialogWrapper open={openAddDialog} onClose={() => { setOpenAddDialog(false); resetFeatureForm(); }} maxWidth="sm" fullWidth>
        <DialogTitle>Add Feature</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField autoFocus margin="normal" label="Feature ID (unique)" fullWidth variant="outlined" required value={newFeatureId} onChange={(e) => setNewFeatureId(e.target.value)} />
            <TextField margin="normal" label="Feature name" fullWidth variant="outlined" value={newFeatureName} onChange={(e) => setNewFeatureName(e.target.value)} />
            <TextField margin="normal" label="Description" fullWidth variant="outlined" value={newFeatureDescription} onChange={(e) => setNewFeatureDescription(e.target.value)} />
            <TextField margin="normal" label="Icon" fullWidth variant="outlined" value={newFeatureIcon} onChange={(e) => setNewFeatureIcon(e.target.value)} placeholder="Optional" />
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'flex-end' }}>
          <Box sx={{ pr: 2 }}>
            <Button onClick={() => { setOpenAddDialog(false); resetFeatureForm(); }} color="primary" variant="contained" sx={{ mr: 1 }}>Cancel</Button>
            <Button onClick={handleCreateFeature} color="primary" variant="contained" disabled={addingFeature || !newFeatureId}>{addingFeature ? <CircularProgress size={20} /> : 'Create'}</Button>
          </Box>
        </DialogActions>
      </DialogWrapper>

      {/* Edit Feature Dialog */}
      <DialogWrapper open={openEditDialog} onClose={() => { setOpenEditDialog(false); setEditingFeature(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Feature</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField margin="normal" label="Feature name" fullWidth variant="outlined" value={newFeatureName} onChange={(e) => setNewFeatureName(e.target.value)} />
            <TextField margin="normal" label="Description" fullWidth variant="outlined" value={newFeatureDescription} onChange={(e) => setNewFeatureDescription(e.target.value)} />
            <TextField margin="normal" label="Icon" fullWidth variant="outlined" value={newFeatureIcon} onChange={(e) => setNewFeatureIcon(e.target.value)} placeholder="Optional" />
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'flex-end' }}>
          <Box sx={{ pr: 2 }}>
            <Button onClick={() => { setOpenEditDialog(false); setEditingFeature(null); }} color="primary" variant="contained" sx={{ mr: 1 }}>Cancel</Button>
            <Button onClick={handleEditFeature} color="primary" variant="contained">Update</Button>
          </Box>
        </DialogActions>
      </DialogWrapper>

      {/* Delete Feature Dialog */}
      <DialogWrapper open={openDeleteDialog} onClose={() => { setOpenDeleteDialog(false); setDeletingFeature(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>Delete Feature</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete feature "{deletingFeature?.name}"? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'flex-end' }}>
          <Box sx={{ pr: 2 }}>
            <Button onClick={() => { setOpenDeleteDialog(false); setDeletingFeature(null); }} color="primary" variant="contained" sx={{ mr: 1 }}>Cancel</Button>
            <Button onClick={handleDeleteFeature} color="error" variant="contained">Delete</Button>
          </Box>
        </DialogActions>
      </DialogWrapper>
    </>
  );
};

export default FeaturesTab;
